/**
 * Streamdown's math plugin uses remark-math, whose native delimiters are
 * dollar fences. The agents use LaTeX's \(...\) and \[...\] delimiters so
 * ordinary single-dollar currency can remain literal. Translate those two
 * forms before Markdown parsing, while leaving code and existing dollar math
 * untouched.
 */

interface CodeFence {
  marker: "`" | "~";
  size: number;
  openingLineEnd: number;
}

function isEscaped(value: string, index: number): boolean {
  let backslashes = 0;
  for (let cursor = index - 1; cursor >= 0 && value[cursor] === "\\"; cursor -= 1) {
    backslashes += 1;
  }
  return backslashes % 2 === 1;
}

function markerRunLength(value: string, index: number, marker: string): number {
  let cursor = index;
  while (cursor < value.length && value[cursor] === marker) cursor += 1;
  return cursor - index;
}

function endOfLine(value: string, index: number): number {
  const newline = value.indexOf("\n", index);
  return newline === -1 ? value.length : newline;
}

function codeFenceAt(value: string, lineStart: number): CodeFence | null {
  let markerStart = lineStart;
  while (markerStart < value.length && markerStart - lineStart < 3 && value[markerStart] === " ") {
    markerStart += 1;
  }

  const marker = value[markerStart];
  if (marker !== "`" && marker !== "~") return null;

  const size = markerRunLength(value, markerStart, marker);
  if (size < 3) return null;

  const openingLineEnd = endOfLine(value, markerStart + size);
  if (marker === "`" && value.slice(markerStart + size, openingLineEnd).includes("`")) {
    return null;
  }
  return { marker, size, openingLineEnd };
}

function codeFenceEnd(value: string, fence: CodeFence): number {
  let lineStart = fence.openingLineEnd < value.length
    ? fence.openingLineEnd + 1
    : value.length;

  while (lineStart < value.length) {
    const lineEnd = endOfLine(value, lineStart);
    let markerStart = lineStart;
    while (markerStart < lineEnd && markerStart - lineStart < 3 && value[markerStart] === " ") {
      markerStart += 1;
    }

    const size = markerRunLength(value, markerStart, fence.marker);
    const suffix = value.slice(markerStart + size, lineEnd).replace(/\r$/, "");
    if (size >= fence.size && /^[ \t]*$/.test(suffix)) {
      return lineEnd < value.length ? lineEnd + 1 : lineEnd;
    }
    lineStart = lineEnd < value.length ? lineEnd + 1 : value.length;
  }

  // An unfinished fence is still code while a reply is streaming.
  return value.length;
}

function codeSpanEnd(value: string, openingStart: number): number {
  const openingSize = markerRunLength(value, openingStart, "`");
  let cursor = openingStart + openingSize;

  while (cursor < value.length) {
    const candidate = value.indexOf("`", cursor);
    if (candidate === -1) return value.length;
    const candidateSize = markerRunLength(value, candidate, "`");
    if (candidateSize === openingSize) return candidate + candidateSize;
    cursor = candidate + candidateSize;
  }
  return value.length;
}

function rawHtmlEnd(value: string, openingStart: number): number | null {
  const opening = /^<(code|pre|script|style|textarea)(?=[\s>])/i.exec(
    value.slice(openingStart),
  );
  if (!opening) return null;

  const closing = new RegExp(`</${opening[1]}\\s*>`, "ig");
  closing.lastIndex = openingStart + opening[0].length;
  const match = closing.exec(value);
  return match ? match.index + match[0].length : value.length;
}

function dollarMathEnd(value: string, openingStart: number): number {
  const openingSize = markerRunLength(value, openingStart, "$");
  let cursor = openingStart + openingSize;

  while (cursor < value.length) {
    const candidate = value.indexOf("$", cursor);
    if (candidate === -1) return value.length;
    if (isEscaped(value, candidate)) {
      cursor = candidate + 1;
      continue;
    }

    const candidateSize = markerRunLength(value, candidate, "$");
    if (candidateSize === openingSize) return candidate + candidateSize;
    cursor = candidate + candidateSize;
  }
  return value.length;
}

/** Find a closing LaTeX delimiter without ending inside a braced group. */
function latexMathEnd(value: string, openingStart: number, closing: "\\)" | "\\]"): number {
  let cursor = openingStart + 2;
  let braceLevel = 0;

  while (cursor < value.length) {
    if (braceLevel <= 0 && value.startsWith(closing, cursor)) return cursor;

    const character = value[cursor];
    if (character === "\\") {
      cursor += 2;
      continue;
    }
    if (character === "{") braceLevel += 1;
    else if (character === "}") braceLevel -= 1;
    cursor += 1;
  }
  return -1;
}

function trimBoundaryLineEndings(value: string): string {
  let result = value;
  if (result.startsWith("\r\n")) result = result.slice(2);
  else if (result.startsWith("\n") || result.startsWith("\r")) result = result.slice(1);

  if (result.endsWith("\r\n")) result = result.slice(0, -2);
  else if (result.endsWith("\n") || result.endsWith("\r")) result = result.slice(0, -1);
  return result;
}

function isOnOwnLine(
  value: string,
  openingStart: number,
  closingEnd: number,
): [boolean, boolean] {
  const lineStart = value.lastIndexOf("\n", openingStart - 1) + 1;
  const lineEnd = endOfLine(value, closingEnd);
  const before = value.slice(lineStart, openingStart).replace(/\r$/, "");
  const after = value.slice(closingEnd, lineEnd).replace(/\r$/, "");
  return [/^[ \t]*$/.test(before), /^[ \t]*$/.test(after)];
}

function displayMath(
  value: string,
  content: string,
  openingStart: number,
  closingEnd: number,
  lineEnding: string,
): string {
  const [openingOnOwnLine, closingOnOwnLine] = isOnOwnLine(
    value,
    openingStart,
    closingEnd,
  );
  const boundary = `${lineEnding}${lineEnding}`;
  const leading = openingOnOwnLine ? "" : boundary;
  const trailing = closingOnOwnLine ? "" : boundary;
  const body = trimBoundaryLineEndings(content);
  return `${leading}$$${lineEnding}${body}${lineEnding}$$${trailing}`;
}

export function normalizeLatexMathDelimiters(markdown: string): string {
  if (!markdown.includes("\\(") && !markdown.includes("\\[")) return markdown;

  const lineEnding = markdown.includes("\r\n") ? "\r\n" : "\n";
  let result = "";
  let cursor = 0;

  while (cursor < markdown.length) {
    const atLineStart = cursor === 0 || markdown[cursor - 1] === "\n";
    if (atLineStart) {
      const fence = codeFenceAt(markdown, cursor);
      if (fence) {
        const end = codeFenceEnd(markdown, fence);
        result += markdown.slice(cursor, end);
        cursor = end;
        continue;
      }
    }

    const htmlEnd = markdown[cursor] === "<" ? rawHtmlEnd(markdown, cursor) : null;
    if (htmlEnd !== null) {
      result += markdown.slice(cursor, htmlEnd);
      cursor = htmlEnd;
      continue;
    }

    if (markdown[cursor] === "`") {
      const end = codeSpanEnd(markdown, cursor);
      result += markdown.slice(cursor, end);
      cursor = end;
      continue;
    }

    if (
      markdown[cursor] === "$"
      && markdown[cursor + 1] === "$"
      && !isEscaped(markdown, cursor)
    ) {
      const end = dollarMathEnd(markdown, cursor);
      result += markdown.slice(cursor, end);
      cursor = end;
      continue;
    }

    if (markdown[cursor] === "\\" && !isEscaped(markdown, cursor)) {
      const delimiter = markdown[cursor + 1];
      if (delimiter === "(" || delimiter === "[") {
        const closing = delimiter === "(" ? "\\)" : "\\]";
        const closingStart = latexMathEnd(markdown, cursor, closing);
        if (closingStart !== -1) {
          const content = markdown.slice(cursor + 2, closingStart);
          const closingEnd = closingStart + 2;
          result += delimiter === "("
            ? `$$${content}$$`
            : displayMath(
                markdown,
                content,
                cursor,
                closingEnd,
                lineEnding,
              );
          cursor = closingEnd;
          continue;
        }
      }
    }

    result += markdown[cursor];
    cursor += 1;
  }

  return result;
}
